/**
 * SetupIncompleteBanner (Phase 43.4).
 *
 * Renders a warning banner when the caller's tenant isn't `active` yet.
 * Different copy + CTA for tenant-admins vs regular members:
 *
 *   tenant-admin   → actionable link to /setup (Phase 44 UI)
 *   super-admin    → same as tenant-admin (super-admins operating on a
 *                    tenant via X-Tenant-Id header should see the same
 *                    "finish setup" nudge for the tenant they're viewing)
 *   member         → passive "your admin is still setting things up"
 *
 * Pulls tenant status from the Zustand store; parent screens that should
 * surface the banner (ProjectList, ProjectView, Coaches) call
 * `loadTenantStatus` on mount and again on window focus. Returns null
 * for `active` tenants (no banner) or while the first fetch is still in
 * flight (avoid flashing stale state).
 *
 * @module components/SetupIncompleteBanner
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../store/useStore.js";

export default function SetupIncompleteBanner() {
  const tenantStatus = useStore((s) => s.tenantStatus);
  const loadTenantStatus = useStore((s) => s.loadTenantStatus);

  useEffect(() => {
    loadTenantStatus();
    const onFocus = () => loadTenantStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadTenantStatus]);

  if (!tenantStatus) return null;
  if (tenantStatus.status === "active") return null;
  // Platform-only super-admin (no tenant selected via the switcher) —
  // null status + null tenantId. Nothing actionable to surface here;
  // the TenantSwitcher handles the "pick a tenant" flow.
  if (!tenantStatus.status || !tenantStatus.tenantId) return null;

  const isAdmin =
    tenantStatus.role === "tenant-admin" || tenantStatus.role === "super-admin";

  return (
    <div
      role="status"
      className={`setup-banner setup-banner--${tenantStatus.status}`}
    >
      <span className="setup-banner__icon" aria-hidden="true">
        ⚠
      </span>
      <div className="setup-banner__body">
        {isAdmin ? (
          <>
            <strong>Setup incomplete.</strong>{" "}
            Finish setup in{" "}
            <Link to="/setup" className="setup-banner__link">
              Settings
            </Link>{" "}
            before running pipelines.
          </>
        ) : (
          <>
            <strong>Tenant setup in progress.</strong>{" "}
            Your administrator is still configuring this workspace. Pipelines
            are disabled until setup is complete.
          </>
        )}
      </div>
    </div>
  );
}
